import React, { createContext, useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import { ThemeProvider } from '@emotion/react';
import { QueryClientProvider, useQuery } from 'react-query';

import Router from '../../../lui/coffee/utils/router';
import { queryClient } from '../queryClient';
import { getAPIStatus, getProfile, getProjects } from '../utils/ApiUtilsV5';
import { GlobalStyle, theme } from '../lumen';
import { AppHeader } from './AppHeader';
import { SideNav } from './SideNav';
import { usePersistedState } from './usePersistedState';

export const ALL_WORKSPACES = 'all';

export const QUERY_KEYS = {
  PROFILE: 'profile',
  PROJECTS: 'projects',
  API_STATUS: 'apiStatus'
};

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error('useWorkspace must be used inside <AppShell>');
  }
  return ctx;
};

const initialsFromProfile = profile => {
  if (!profile) return 'U';
  const source = profile.full_name || profile.username || 'U';
  const parts = source.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

export const AppShell = ({ activeTab = 'projects', children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <AppShellInner activeTab={activeTab}>{children}</AppShellInner>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const AppShellInner = ({ activeTab, children }) => {
  const { data: profile } = useQuery(QUERY_KEYS.PROFILE, getProfile);
  // Workspaces come from the same /profile/ payload — derive them instead of
  // firing a second identical request.
  const workspaces = profile?.workspaces ?? [];
  const { data: projects = [], isLoading: projectsLoading } = useQuery(
    QUERY_KEYS.PROJECTS,
    () => getProjects().then(map => Object.values(map || {})),
    {
      refetchInterval: data => {
        const anyBuildingSentiment = (data || []).some(p => {
          const s = p?.last_build_info?.sentiment;
          return s?.start_time != null && s?.stop_time == null;
        });
        return anyBuildingSentiment ? 30000 : false;
      }
    }
  );
  const { data: apiStatus = null } = useQuery(
    QUERY_KEYS.API_STATUS,
    getAPIStatus
  );

  const [savedWorkspaceId, setSavedWorkspaceId] = usePersistedState(
    'projectListWorkspaceId',
    null
  );

  const resolvedWorkspaceId = useMemo(() => {
    if (workspaces.length === 0) return savedWorkspaceId;
    if (savedWorkspaceId === ALL_WORKSPACES) return ALL_WORKSPACES;
    if (
      savedWorkspaceId &&
      workspaces.some(w => w.workspace_id === savedWorkspaceId)
    ) {
      return savedWorkspaceId;
    }
    return workspaces[0].workspace_id;
  }, [savedWorkspaceId, workspaces]);

  const isAllWorkspaces = resolvedWorkspaceId === ALL_WORKSPACES;
  const currentWorkspace = isAllWorkspaces
    ? null
    : workspaces.find(w => w.workspace_id === resolvedWorkspaceId) || null;

  const workspaceContextValue = useMemo(
    () => ({
      workspaces,
      workspaceId: resolvedWorkspaceId,
      isAllWorkspaces,
      currentWorkspace,
      setWorkspaceId: setSavedWorkspaceId,
      projects,
      projectsLoading,
      profile,
      apiStatus
    }),
    [
      workspaces,
      resolvedWorkspaceId,
      isAllWorkspaces,
      currentWorkspace,
      setSavedWorkspaceId,
      projects,
      projectsLoading,
      profile,
      apiStatus
    ]
  );

  const displayedWorkspaceName =
    workspaces.length === 0
      ? ''
      : isAllWorkspaces
        ? 'All workspaces'
        : currentWorkspace?.name || '';

  const projectCountsByWorkspace = useMemo(() => {
    const counts = {};
    for (const p of projects) {
      counts[p.workspace_id] = (counts[p.workspace_id] || 0) + 1;
    }
    return counts;
  }, [projects]);

  const workspacesForNav = useMemo(() => {
    if (workspaces.length === 0) return [];
    const real = workspaces.map(w => ({
      workspace_id: w.workspace_id,
      name: w.name,
      project_count: projectCountsByWorkspace[w.workspace_id] || 0
    }));
    if (real.length > 1) {
      return [
        {
          workspace_id: ALL_WORKSPACES,
          name: 'All workspaces',
          project_count: projects.length
        },
        ...real
      ];
    }
    return real;
  }, [workspaces, projectCountsByWorkspace, projects.length]);

  return (
    <WorkspaceContext.Provider value={workspaceContextValue}>
      <Root>
        <AppHeader
          userInitials={initialsFromProfile(profile)}
          onSettings={() => Router.go('/app/settings/profile')}
          onLogout={() => Router.go('login.html')}
        />
        <Body>
          <SideNav
            activeTab={activeTab}
            workspaceName={displayedWorkspaceName}
            workspaces={workspacesForNav}
            onWorkspaceChange={setSavedWorkspaceId}
            onSelectSettings={() => Router.go('/app/settings/profile')}
          />
          <Content>{children}</Content>
        </Body>
      </Root>
    </WorkspaceContext.Provider>
  );
};

const Root = styled.div`
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fdfeff;
`;

const Body = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;
  min-height: 0;
`;

const Content = styled.main`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 30px 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
